// pdfUtils.js
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

// ─── Image Paths (single source of truth) ───
const HEADER_IMAGE = "/si_header1.png";
const FOOTER_IMAGE = "/si_footer1.png";

// ─── Convert image URL to base64 ───
const imageToBase64 = (imageSrc) => {
  return new Promise((resolve) => {
    if (typeof imageSrc === "string" && imageSrc.startsWith("data:")) {
      resolve(imageSrc);
      return;
    }
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      console.warn(`Image load failed: ${imageSrc}`);
      resolve(null);
    };
    img.src = imageSrc;
  });
};

// ─── Cache to avoid re-converting every time ───
let cachedHeader = null;
let cachedFooter = null;

// ─── Load & Cache both images once ───
export const loadPdfImages = async () => {
  if (!cachedHeader) {
    try {
      cachedHeader = await imageToBase64(HEADER_IMAGE);
    } catch (e) {
      console.warn("Header image failed:", e);
    }
  }

  if (!cachedFooter) {
    try {
      cachedFooter = await imageToBase64(FOOTER_IMAGE);
    } catch (e) {
      console.warn("Footer image failed:", e);
    }
  }

  return {
    headerBase64: cachedHeader,
    footerBase64: cachedFooter,
  };
};

// ─── Shared Header Builder ───
export const getPdfHeader = (headerBase64) => {
  if (!headerBase64) return () => ({ text: "", margin: [0, 0, 0, 0] });

  return () => ({
    image: headerBase64,
    width: 595,
    margin: [0, 0, 0, 0],
  });
};

// ─── Shared Footer Builder (with optional page numbers) ───
export const getPdfFooter = (footerBase64, showPageNumbers = true) => {
  return (currentPage, pageCount) => {
    const stack = [];

    if (footerBase64) {
      stack.push({
        image: footerBase64,
        width: 595,
        height: 90,
        margin: [0, 0, 0, 0],
      });
    }

    if (showPageNumbers) {
      stack.push({
        text: `Page ${currentPage} of ${pageCount}`,
        alignment: "center",
        fontSize: 8,
        margin: [0, footerBase64 ? 4 : 10, 0, 0],
      });
    }

    return { stack };
  };
};

// ─── Shared Page Margins (consistent across all PDFs) ───
export const PDF_PAGE_MARGINS = [30, 110, 30, 90];

// ─── Shared PDF Generator ───
export const createAndOpenPdf = (docDefinition, action = "open", fileName = "document") => {
  const pdfDoc = pdfMake.createPdf(docDefinition);

  if (action === "download") {
    pdfDoc.download(`${fileName}_${Date.now()}.pdf`);
  } else {
    pdfDoc.open();
  }
};